/**
 * FOOTER COMPONENT
 * 
 * Site-wide footer with:
 * - Links to Terms, Privacy, Contact
 * - Social links (optional)
 * - Copyright notice
 * - Back to top button
 * 
 * Mobile: 2-column grid for compactness
 * Desktop: 4-column grid
 */

import { Link } from 'react-router-dom';
import { Heart, ArrowUp, Mail, Instagram, Coffee, Facebook } from 'lucide-react';

// Custom icons for platforms not in Lucide
const DiscordIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.96s-.37-.74-.37-1.82c0-1.7 1-2.97 2.24-2.97 1.06 0 1.57.8 1.57 1.75 0 1.06-.68 2.65-1.03 4.13-.3 1.24.62 2.26 1.84 2.26 2.2 0 3.9-2.33 3.9-5.7 0-2.97-2.14-5.05-5.2-5.05-3.54 0-5.61 2.65-5.61 5.4 0 1.07.4 2.22.92 2.84.1.12.12.23.08.36l-.34 1.4c-.06.23-.18.28-.42.17-1.56-.73-2.54-3-2.54-4.83 0-3.93 2.86-7.54 8.24-7.54 4.33 0 7.7 3.08 7.7 7.2 0 4.3-2.7 7.76-6.46 7.76-1.26 0-2.45-.66-2.86-1.43l-.78 2.96c-.28 1.08-1.04 2.44-1.55 3.27A12 12 0 1 0 12 0z"/>
  </svg>
);

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="mt-auto">
      {/* Main Footer */}
      <div className="backdrop-blur-md bg-slate-800/60 border-t border-slate-600">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {/* Brand - Full width on mobile */}
            <div className="col-span-2 md:col-span-1">
              <Link
                to="/"
                className="text-lg md:text-xl text-glass hover:text-glass-secondary transition-default font-heading"
              >
                Polish ALL the Things!
              </Link>
              <p className="mt-2 text-xs md:text-sm text-glass-secondary font-body">
                The ultimate nail art collection manager for enthusiasts and professionals alike.
              </p>
            </div>

            {/* Explore */}
            <div>
              <h3 className="font-bold text-glass mb-2 md:mb-3 text-sm md:text-base font-body">
                Explore
              </h3>
              <ul className="space-y-1 md:space-y-2 text-xs md:text-sm font-body">
                <li>
                  <Link to="/about" className="text-glass-secondary hover:text-glass transition-default">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/discover" className="text-glass-secondary hover:text-glass transition-default">
                    Discover
                  </Link>
                </li>
                <li>
                  <Link to="/events" className="text-glass-secondary hover:text-glass transition-default">
                    Events Calendar
                  </Link>
                </li>
                <li>
                  <Link to="/marketplace" className="text-glass-secondary hover:text-glass transition-default">
                    Marketplace
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-glass-secondary hover:text-glass transition-default">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            {/* Help */}
            <div>
              <h3 className="font-bold text-glass mb-2 md:mb-3 text-sm md:text-base font-body">
                Help
              </h3>
              <ul className="space-y-1 md:space-y-2 text-xs md:text-sm font-body">
                <li>
                  <Link to="/contact" className="text-glass-secondary hover:text-glass transition-default">
                    Contact Us
                  </Link>
                </li>
                <li>
                <Link to="/dmcaclaim" className="text-glass-secondary hover:text-glass transition-default">
                  DMCA Claim Form
                </Link>
              </li>
                <li>
                  <Link to="/help" className="text-glass-secondary hover:text-glass transition-default">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/feedback" className="text-glass-secondary hover:text-glass transition-default">
                    Feedback & Bugs
                  </Link>
                </li>
                {/*<li>*/}
                {/*  <Link to="/guides" className="text-glass-secondary hover:text-glass transition-default">*/}
                {/*    Guides*/}
                {/*  </Link>*/}
                {/*</li>*/}
                {/*<li>*/}
                {/*  <Link to="/updates" className="text-glass-secondary hover:text-glass transition-default">*/}
                {/*    Updates*/}
                {/*  </Link>*/}
                {/*</li>*/}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-bold text-glass mb-2 md:mb-3 text-sm md:text-base font-body">
                Legal
              </h3>
              <ul className="space-y-1 md:space-y-2 text-xs md:text-sm font-body">
                <li>
                  <Link to="/copyright" className="text-glass-secondary hover:text-glass transition-default">
                    Copyright Policy
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-glass-secondary hover:text-glass transition-default">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="text-glass-secondary hover:text-glass transition-default">
                    Refund Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-glass-secondary hover:text-glass transition-default">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-slate-900/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
            {/* Copyright */}
            <p className="text-xs md:text-sm text-glass-muted font-body text-center sm:text-left">
              © {currentYear}{' '}
              <a
                href="https://bluprint.design/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-pink-400 transition-default"
              >
                BluPrint Design
              </a>
              . Made with{' '}
              <Heart className="w-3 h-3 md:w-4 md:h-4 text-pink-400 fill-pink-400 inline-block align-text-bottom" />{' '}
              for nail art lovers.
            </p>

            {/* Social Links & Back to Top */}
            <div className="flex items-center gap-3 md:gap-4">
              {/* Social Icons */}
              <a
              href="https://discord.gg/QwDyUDwHdz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-glass-muted hover:text-pink-400 transition-default"
              aria-label="Discord"
              >
                <DiscordIcon className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <a
              href="mailto:hello@polishallthethings.com"
              className="text-glass-muted hover:text-pink-400 transition-default"
              aria-label="Email"
              >
                <Mail className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <a
              href="https://facebook.com/polishALLtheThings"
              target="_blank"
              rel="noopener noreferrer"
              className="text-glass-muted hover:text-pink-400 transition-default"
              aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <div className="flex items-center gap-2 md:gap-3">
                <a
                  href="https://instagram.com/polish_all_thethings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-glass-muted hover:text-pink-400 transition-default"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4 md:w-5 md:h-5" />
                </a>
                <a
                href="https://ko-fi.com/polishallthethings"
                target="_blank"
                rel="noopener noreferrer"
                className="text-glass-muted hover:text-pink-400 transition-default"
                aria-label="Support on Ko-fi"
                >
                  <Coffee className="w-4 h-4 md:w-5 md:h-5" />
                </a>
                <a
                href="https://pinterest.com/polishallthethings"
                target="_blank"
                rel="noopener noreferrer"
                className="text-glass-muted hover:text-pink-400 transition-default"
                aria-label="Pinterest"
                >
                  <PinterestIcon className="w-4 h-4 md:w-5 md:h-5" />
                </a>
                <a
                  href="https://tiktok.com/@polish_all_thethings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-glass-muted hover:text-pink-400 transition-default"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="w-4 h-4 md:w-5 md:h-5" />
                </a>
              </div>

              {/* Divider */}
              <div className="w-px h-4 md:h-5 bg-slate-600"></div>

              {/* Back to Top */}
              <button
                onClick={scrollToTop}
                className="flex items-center gap-1 text-xs md:text-sm text-glass-muted hover:text-glass transition-default font-body"
              >
                <ArrowUp className="w-3 h-3 md:w-4 md:h-4" />
                Top
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
